import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { ScanLine, Check, X, PackagePlus } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Fiziksel barkod okuyucu (klavye emülasyonlu) için metin kutusu.
 * - Enter'da VEYA saf-rakam 8+ hane yazım durduğunda otomatik işler (Enter göndermeyen okuyucular için).
 * - Her işlemden sonra kutuyu TEMİZLER → okunmayan/yanlış kod bir sonraki taramaya eklenmez (metin birikmez).
 * - Bulunanda yeşil, bulunamayanda kırmızı geri bildirim + son 3 okuma şeridi.
 * onResolve(code) => Promise<{ ok: boolean, label: string }>  (üst bileşen ürünü ekler + uyarı toast'ını gösterir)
 */
export default function BarcodeInput({ onResolve, onAddNew, placeholder = "Barkod okut + Enter", autoFocus = false, testid = "barcode-input", className }) {
  const [text, setText] = useState("");
  const [recent, setRecent] = useState([]);
  const [flash, setFlash] = useState(null); // "ok" | "err" | null
  const [lastFail, setLastFail] = useState(""); // bulunamayan son barkod → "yeni ürün ekle" kısayolu
  const ref = useRef(null);
  const debRef = useRef(null);
  const busyRef = useRef(false);
  const lastRef = useRef({ code: "", at: 0 });

  useEffect(() => {
    if (!autoFocus) return undefined;
    const t = setTimeout(() => { try { ref.current && ref.current.focus(); } catch { /* noop */ } }, 220);
    return () => clearTimeout(t);
  }, [autoFocus]);

  useEffect(() => () => { if (debRef.current) clearTimeout(debRef.current); }, []);

  const process = async (raw) => {
    if (debRef.current) { clearTimeout(debRef.current); debRef.current = null; }
    const v = String(raw || "").trim();
    setText("");   // her durumda temizle → yanlış/kalıntı kod birikmez
    if (!v || busyRef.current) return;
    const now = Date.now();
    if (v === lastRef.current.code && now - lastRef.current.at < 400) return;  // Enter + otomatik yarışını engelle
    lastRef.current = { code: v, at: now };
    busyRef.current = true;
    try {
      const res = await onResolve(v);
      const ok = !!(res && res.ok);
      setFlash(ok ? "ok" : "err");
      setLastFail(ok ? "" : v);
      setRecent((r) => [{ ok, label: (res && res.label) || v }, ...r].slice(0, 3));
    } catch {
      setFlash("err");
      setLastFail(v);
      setRecent((r) => [{ ok: false, label: v }, ...r].slice(0, 3));
    } finally {
      busyRef.current = false;
      setTimeout(() => setFlash(null), 550);
      try { ref.current && ref.current.focus(); } catch { /* noop */ }
    }
  };

  const onChange = (e) => {
    const val = e.target.value;
    setText(val);
    if (debRef.current) clearTimeout(debRef.current);
    const cand = val.trim();
    // Enter göndermeyen okuyucular: saf rakam ve 8+ hane ise yazım durduktan 250 ms sonra otomatik işle
    if (/^\d{8,}$/.test(cand)) debRef.current = setTimeout(() => process(cand), 250);
  };

  const onKeyDown = (e) => { if (e.key === "Enter") { e.preventDefault(); process(text); } };

  return (
    <div className={className}>
      <div className="relative">
        <ScanLine className={cn("absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none", flash === "ok" ? "text-green-500" : flash === "err" ? "text-red-500" : "text-primary")} />
        <Input
          ref={ref}
          value={text}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          data-testid={testid}
          className={cn("h-9 pl-8 pr-8 font-mono transition-colors", flash === "ok" && "border-green-500 ring-1 ring-green-500", flash === "err" && "border-red-500 ring-1 ring-red-500")}
        />
        {flash === "ok" && <Check className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" data-testid={`${testid}-ok`} />}
        {flash === "err" && <X className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-red-500" data-testid={`${testid}-err`} />}
      </div>
      {recent.length > 0 && (
        <div className="flex gap-1.5 mt-1.5 flex-wrap" data-testid={`${testid}-recent`}>
          {recent.map((r, i) => (
            <div key={i} className={cn("flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium max-w-[220px]", r.ok ? "border-green-500/40 bg-green-500/10 text-green-600" : "border-red-500/40 bg-red-500/10 text-red-600")} data-testid={`${testid}-recent-${i}`}>
              {r.ok ? <Check className="h-3.5 w-3.5 shrink-0" /> : <X className="h-3.5 w-3.5 shrink-0" />}
              <span className="truncate">{r.label}</span>
            </div>
          ))}
        </div>
      )}
      {onAddNew && lastFail && (
        <button
          type="button"
          onClick={() => { const c = lastFail; setLastFail(""); onAddNew(c); }}
          className="mt-1.5 inline-flex items-center gap-1.5 rounded-md border border-primary/50 bg-primary/10 px-2.5 py-1.5 text-xs font-semibold text-primary hover:bg-primary/20 transition-colors"
          data-testid={`${testid}-add-new`}
        >
          <PackagePlus className="h-3.5 w-3.5" />"{lastFail}" barkodlu yeni ürün ekle
        </button>
      )}
    </div>
  );
}
